(() => {
  if (window.variantPickerLayout2Loaded) return;
  window.variantPickerLayout2Loaded = true;

  let activeDrawer = null;
  let lastFocusedElement = null;
  const pickerVariantAvailability = new WeakMap();

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const normalizeCategories = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
    return [];
  };

  const syncLabelState = (input, scope) => {
    if (!input) return;

    const groupName = input.getAttribute('name');
    if (!groupName) return;

    const root = scope instanceof Element ? scope : input.closest('[data-vpl2-picker]') || document;

    root.querySelectorAll(`input[name="${CSS.escape(groupName)}"]`).forEach((radio) => {
      const label = root.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      label?.classList.toggle('is-active', radio.checked);
    });
  };

  const getCustomSwatchRoot = (target) => target.closest('[data-vpl2-custom-swatch]');

  const isMtoPicker = (el) => Boolean(el?.closest?.('[data-vpl2-mto-picker]'));

  const getLayout2Pickers = () =>
    [...document.querySelectorAll('[data-vpl2-picker]')].filter((picker) => !isMtoPicker(picker));

  const getDrawerForRoot = (root) => {
    const sectionId = root?.getAttribute('data-vpl2-section');
    if (!sectionId) return null;
    return document.getElementById(`Vpl2MaterialDrawer-${sectionId}`);
  };

  const normalizeToken = (value) => String(value || '').trim().toLowerCase();

  const splitTokens = (value) =>
    String(value || '')
      .split('||')
      .map((token) => token.trim())
      .filter(Boolean);

  const isMaterialOptionName = (name) => {
    const token = normalizeToken(name);
    return token === 'material' || token === 'materials';
  };

  const isAvailabilityOptionName = (name) => normalizeToken(name) === 'availability';

  const isVisibleOption = (input) => Boolean(input) && !input.closest('[hidden]');

  const getSelectedOptionValue = (root, isOptionName) => {
    const picker = root?.closest('[data-vpl2-picker]');
    if (!picker) return '';

    const optionInputs = [...picker.querySelectorAll('input[data-variant-input]')].filter((input) =>
      isOptionName(input.getAttribute('name'))
    );

    if (!optionInputs.length) return '';

    const checked = optionInputs.find((input) => input.checked);
    return (checked?.value || '').trim();
  };

  const getSelectedMaterial = (root) => getSelectedOptionValue(root, isMaterialOptionName);

  const getSelectedAvailability = (root) => getSelectedOptionValue(root, isAvailabilityOptionName);

  // Normalize "In Stock" / "Instock" / "Made-to-Order" for reliable matching.
  const normalizeMatchToken = (value) => normalizeToken(value).replace(/[\s_-]+/g, '');

  const normalizeTitleToken = (value) => normalizeMatchToken(value);

  const getProductInStockColorNames = (root) => {
    const dataNode = root?.querySelector('[data-vpl2-in-stock-colors]');
    if (!dataNode) return [];

    try {
      const parsed = JSON.parse(dataNode.textContent || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map((name) => String(name || '').trim()).filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  const matchesInStockTitle = (itemTitle, inStockNames) => {
    if (!inStockNames.length) return false;

    const titleToken = normalizeTitleToken(itemTitle);
    if (!titleToken) return false;

    return inStockNames.some((name) => normalizeTitleToken(name) === titleToken);
  };

  const isInStockAvailabilityValue = (value) => normalizeMatchToken(value) === 'instock';

  const getOutOfStockMessage = (picker) =>
    String(picker?.getAttribute('data-vpl2-out-of-stock-message') || '').trim();

  const updateAvailabilityShippingMessage = (picker, variantAvailable = null) => {
    const message = picker?.querySelector('[data-vpl2-shipping-message]');
    if (!message) return;

    const selectedInput = [...picker.querySelectorAll('input[data-variant-input]:checked')].find((input) =>
      isAvailabilityOptionName(input.getAttribute('name'))
    );

    const defaultMessage = selectedInput?.getAttribute('data-vpl2-shipping-message-value') || '';
    const outOfStockMessage = getOutOfStockMessage(picker);

    if (variantAvailable === false && outOfStockMessage) {
      message.textContent = outOfStockMessage;
      return;
    }

    message.textContent = defaultMessage;
  };

  // Material/Availability drive the Custom Swatch list, so keep them open together.
  const isCustomSwatchTriggerRow = (row) => {
    const optionName = row?.querySelector('input[data-variant-input]')?.getAttribute('name');
    return isMaterialOptionName(optionName) || isAvailabilityOptionName(optionName);
  };

  const openRow = (row) => {
    if (!row || row.hasAttribute('hidden') || row.classList.contains('vpl2-hidden-option')) return;
    row.classList.add('is-open');
    row.querySelector('[data-vpl2-toggle]')?.setAttribute('aria-expanded', 'true');
  };

  const openCustomSwatchRows = (picker) => {
    picker?.querySelectorAll('[data-vpl2-custom-swatch]').forEach(openRow);
  };

  const openMaterialRows = (picker) => {
    picker?.querySelectorAll('[data-vpl2-row]').forEach((row) => {
      const optionName = row.querySelector('input[data-variant-input]')?.getAttribute('name');
      if (isMaterialOptionName(optionName)) openRow(row);
    });
  };

  const matchesOptionValue = (itemValue, selectedValue) => {
    // No option / no selection => keep previous "show all" behavior.
    if (!selectedValue) return true;
    // When an option is selected, only exact matches are shown.
    if (!itemValue) return false;
    return normalizeMatchToken(itemValue) === normalizeMatchToken(selectedValue);
  };

  const matchesMaterial = (itemMaterial, selectedMaterial) =>
    matchesOptionValue(itemMaterial, selectedMaterial);

  const matchesAvailability = (itemAvailability, selectedAvailability) =>
    matchesOptionValue(itemAvailability, selectedAvailability);

  const normalizeAvailabilityValues = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
    return [];
  };

  const getSelectedCustomSwatchValues = (root) => {
    const category =
      [...(root?.querySelectorAll('[data-vpl2-category-input]:checked') || [])]
        .find(isVisibleOption)
        ?.value?.trim() || '';
    const swatch =
      [...(root?.querySelectorAll('[data-vpl2-swatch-input]:checked') || [])]
        .find(isVisibleOption)
        ?.value?.trim() || '';
    return { category, swatch };
  };

  const updateCustomSwatchSummary = (root) => {
    if (!root) return;

    const { category, swatch } = getSelectedCustomSwatchValues(root);
    const categoryDisplay = root.querySelector('[data-vpl2-category-display]');
    const swatchDisplays = root.querySelectorAll('[data-vpl2-swatch-display]');
    const categoryProperty = root.querySelector('[data-vpl2-category-property]');
    const swatchProperty = root.querySelector('[data-vpl2-swatch-property]');

    if (categoryDisplay) categoryDisplay.textContent = category;
    swatchDisplays.forEach((el) => {
      el.textContent = swatch;
    });
    if (categoryProperty) categoryProperty.value = category;
    if (swatchProperty) swatchProperty.value = swatch;
  };

  const getCustomSwatchRootsForForm = (form) => {
    if (!(form instanceof HTMLFormElement) || !form.id) return [];

    return [
      ...document.querySelectorAll(`[data-vpl2-custom-swatch][data-vpl2-form="${CSS.escape(form.id)}"]`),
    ].filter((root) => !isMtoPicker(root));
  };

  const syncCustomSwatchPropertiesToFormData = (form, formData) => {
    if (!(form instanceof HTMLFormElement) || !formData) return;

    const roots = getCustomSwatchRootsForForm(form);
    if (!roots.length) return;

    roots.forEach((root) => {
      updateCustomSwatchSummary(root);

      const { category, swatch } = getSelectedCustomSwatchValues(root);

      // Only touch Custom Swatch keys for layout2 products that render this block.
      if (swatch) formData.set('properties[Custom Swatch]', swatch);
      else formData.delete('properties[Custom Swatch]');

      if (category) formData.set('properties[Color Category]', category);
      else formData.delete('properties[Color Category]');
    });
  };

  const filterCustomSwatches = (root) => {
    if (!root) return;

    const selectedMaterial = getSelectedMaterial(root);
    const selectedAvailability = getSelectedAvailability(root);
    const productInStockNames = getProductInStockColorNames(root);
    const restrictToProductInStockColors =
      isInStockAvailabilityValue(selectedAvailability) &&
      (root.hasAttribute('data-vpl2-has-in-stock-colors') || productInStockNames.length > 0);
    const categoryItems = [...root.querySelectorAll('[data-vpl2-category-item]')];
    const items = [...root.querySelectorAll('[data-vpl2-swatch-item]')];

    const itemMatchesBaseFilters = (item) => {
      const itemMaterial = item.getAttribute('data-material') || '';
      const itemAvailabilities = splitTokens(item.getAttribute('data-availabilities'));
      const itemTitle = item.getAttribute('data-title') || '';
      const matchesSelectedMaterial = matchesMaterial(itemMaterial, selectedMaterial);
      const matchesProductInStockColor =
        item.getAttribute('data-in-stock-on-product') === 'true' ||
        matchesInStockTitle(itemTitle, productInStockNames);

      if (restrictToProductInStockColors) {
        return matchesSelectedMaterial && matchesProductInStockColor;
      }

      const matchesSelectedAvailability =
        !selectedAvailability ||
        itemAvailabilities.some((availability) =>
          matchesAvailability(availability, selectedAvailability)
        );

      return matchesSelectedMaterial && matchesSelectedAvailability;
    };

    const visibleCategoryNames = new Set();
    items.forEach((item) => {
      if (!itemMatchesBaseFilters(item)) return;
      splitTokens(item.getAttribute('data-categories')).forEach((categoryName) => {
        visibleCategoryNames.add(categoryName);
      });
    });

    categoryItems.forEach((item) => {
      const materials = splitTokens(item.getAttribute('data-materials'));
      const availabilities = splitTokens(item.getAttribute('data-availabilities'));
      const categoryName =
        item.getAttribute('data-category') ||
        item.querySelector('[data-vpl2-category-input]')?.value ||
        '';
      // No Material/Availability option => show all. Otherwise only linked categories.
      const matchesMaterialFilter =
        !selectedMaterial ||
        materials.some((material) => matchesMaterial(material, selectedMaterial));
      const matchesAvailabilityFilter =
        !selectedAvailability ||
        availabilities.some((availability) =>
          matchesAvailability(availability, selectedAvailability)
        );
      const matches =
        restrictToProductInStockColors
          ? matchesMaterialFilter && visibleCategoryNames.has(categoryName)
          : matchesMaterialFilter && matchesAvailabilityFilter;

      item.hidden = !matches;

      const input = item.querySelector('[data-vpl2-category-input]');
      if (!matches && input) {
        input.checked = false;
        document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.remove('is-active');
      }
    });

    const visibleCategoryInputs = categoryItems
      .filter((item) => !item.hidden)
      .map((item) => item.querySelector('[data-vpl2-category-input]'))
      .filter(Boolean);

    let selectedCategory = visibleCategoryInputs.find((input) => input.checked) || null;

    if (!selectedCategory && visibleCategoryInputs.length) {
      selectedCategory = visibleCategoryInputs[0];
      selectedCategory.checked = true;
      syncLabelState(selectedCategory);
    }

    const category = selectedCategory?.value || '';
    let visibleChecked = null;

    items.forEach((item) => {
      const categories = splitTokens(item.getAttribute('data-categories'));
      const matchesCategory =
        !category || categories.length === 0 || categories.includes(category);
      const isVisible = itemMatchesBaseFilters(item) && matchesCategory;

      item.hidden = !isVisible;

      const input = item.querySelector('[data-vpl2-swatch-input]');
      if (!isVisible && input) {
        input.checked = false;
        document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.remove('is-active');
      }
      if (isVisible && input?.checked) visibleChecked = input;
    });

    if (!visibleChecked) {
      const firstVisible = items
        .find((item) => !item.hidden)
        ?.querySelector('[data-vpl2-swatch-input]');

      if (firstVisible) {
        firstVisible.checked = true;
        syncLabelState(firstVisible);
      }
    }

    const visibleSwatches = items.filter((item) => !item.hidden);
    const hasVisibleSwatches = visibleSwatches.length > 0;
    const visibleCategories = categoryItems.filter((item) => !item.hidden);
    const hasVisibleCategories = visibleCategories.length > 0;

    const noSwatchesMsg = root.querySelector('[data-vpl2-no-swatches-msg]');
    const categoryFieldset = root.querySelector('.vpl2-category-grid')?.closest('fieldset');
    const divider = root.querySelector('.vpl2-divider');

    if (noSwatchesMsg) {
      noSwatchesMsg.hidden = hasVisibleSwatches;
    }

    if (categoryFieldset) {
      categoryFieldset.hidden = !hasVisibleCategories || !hasVisibleSwatches;
    }

    if (divider) {
      divider.hidden = !hasVisibleCategories || !hasVisibleSwatches;
    }

    updateCustomSwatchSummary(root);
  };

  // Theme variant JS updates radio.checked without firing `change`.
  // Scoped to [data-vpl2-picker] only — never hides Availability/Material buttons.
  const syncPickerState = (picker) => {
    if (!(picker instanceof Element) || !picker.hasAttribute('data-vpl2-picker')) return;

    picker.querySelectorAll('[data-vpl2-row]').forEach((row) => {
      // Skip Custom Swatch row here; it has no variant inputs.
      if (row.hasAttribute('data-vpl2-custom-swatch')) return;

      const checked = row.querySelector('input[data-variant-input]:checked');
      if (!checked) return;

      // Hidden Color/Swatch rows still keep inputs for variant matching.
      if (!row.hasAttribute('hidden') && !row.classList.contains('vpl2-hidden-option')) {
        const rowIndex = row.getAttribute('data-vpl2-row');
        const displayLabel = picker.querySelector(`[data-vpl2-sel-display="${rowIndex}"]`);
        if (displayLabel) displayLabel.textContent = checked.value;
        syncLabelState(checked, picker);
      }
    });

    const variantAvailable = pickerVariantAvailability.has(picker)
      ? pickerVariantAvailability.get(picker)
      : null;
    updateAvailabilityShippingMessage(picker, variantAvailable);

    // Re-apply Material/Availability → category/swatch filters from checked option inputs.
    picker.querySelectorAll('[data-vpl2-custom-swatch]').forEach((customRoot) => {
      filterCustomSwatches(customRoot);
    });
  };

  const SPEC_LABELS = [
    'Country of Origin',
    'Pilling Resistance',
    'Cleaning Code',
    'Fabric Types',
    'Certifications',
    'Martindale',
    'Protection',
    'Direction',
    'Abrasion',
    'Contains',
    'Content',
    'Material',
    'Color',
    'Grade',
  ];

  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const parseLabelValuePairs = (text) => {
    const source = String(text || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    if (!source) return [];

    const labelPattern = new RegExp(
      `\\b(${SPEC_LABELS.map(escapeRegExp).join('|')})\\s*:\\s*`,
      'gi'
    );
    const labels = [];
    let match;

    while ((match = labelPattern.exec(source)) !== null) {
      labels.push({
        label: match[1].trim(),
        valueStart: match.index + match[0].length,
        index: match.index,
      });
    }

    return labels
      .map((item, index) => {
        const valueEnd = index + 1 < labels.length ? labels[index + 1].index : source.length;
        const value = source.slice(item.valueStart, valueEnd).trim();
        if (!item.label || !value) return null;
        return { label: item.label, value };
      })
      .filter(Boolean);
  };

  const parseDescriptionSpecs = (descriptionHtml) => {
    if (!descriptionHtml) return [];

    const lineText = descriptionHtml
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h\d)>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\u00a0/g, ' ');

    const lineSpecs = [];
    lineText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const singleLine = line.match(/^([^:]+):\s*(.+)$/);
        if (singleLine && !parseLabelValuePairs(line).slice(1).length) {
          lineSpecs.push({
            label: singleLine[1].trim(),
            value: singleLine[2].trim(),
          });
          return;
        }

        parseLabelValuePairs(line).forEach((spec) => lineSpecs.push(spec));
      });

    if (lineSpecs.length) return lineSpecs;

    return parseLabelValuePairs(lineText);
  };

  const buildDrawerContent = (entry, drawer) => {
    const fabricLabel = drawer.getAttribute('data-label-fabric') || 'Fabric Types';
    const gradeLabel = drawer.getAttribute('data-label-grade') || 'Grade';
    const descriptionLabel = drawer.getAttribute('data-label-description') || 'Description:';

    const fabricValue = entry.content || entry.material || '';
    const gradeMatch = parseDescriptionSpecs(entry.descriptionHtml || '').find((spec) =>
      /^grade$/i.test(spec.label)
    );
    const gradeValue = gradeMatch?.value || '';

    const metaParts = [];
    if (fabricValue) metaParts.push(`<strong>${escapeHtml(fabricLabel)}:</strong> ${escapeHtml(fabricValue)}`);
    if (gradeValue) metaParts.push(`<strong>${escapeHtml(gradeLabel)}:</strong> ${escapeHtml(gradeValue)}`);

    const specs = parseDescriptionSpecs(entry.descriptionHtml || '').filter(
      (spec) => !/^(grade|colou?r)$/i.test(spec.label)
    );

    const imageSrc = entry.imageLarge || entry.image;
    const imageHtml = imageSrc
      ? `<img class="vpl2-drawer__image" src="${escapeHtml(imageSrc)}" alt="${escapeHtml(entry.title)}" width="640" height="640" loading="lazy">`
      : `<div class="vpl2-drawer__image vpl2-drawer__image--fallback" ${entry.color ? `data-vpl2-color="${escapeHtml(entry.color)}"` : ''}></div>`;

    const specsHtml = specs.length
      ? `<ol class="vpl2-drawer__specs">
          ${specs
            .map(
              (spec, index) => `
                <li class="vpl2-drawer__spec">
                  <span class="vpl2-drawer__spec-index">${index + 1}.</span>
                  <span class="vpl2-drawer__spec-copy">
                    <strong>${escapeHtml(spec.label)}:</strong> ${escapeHtml(spec.value)}
                  </span>
                </li>
              `
            )
            .join('')}
        </ol>`
      : entry.descriptionHtml
        ? `<div class="vpl2-drawer__rte rte">${entry.descriptionHtml}</div>`
        : '';

    return `
      <div class="vpl2-drawer__card">
        ${imageHtml}
        <h3 class="vpl2-drawer__name">${escapeHtml(entry.title)}</h3>
        ${metaParts.length ? `<p class="vpl2-drawer__meta">${metaParts.join('<span class="vpl2-drawer__meta-dot">•</span>')}</p>` : ''}
        ${
          specsHtml
            ? `<div class="vpl2-drawer__description">
                <h4 class="vpl2-drawer__description-title">${escapeHtml(descriptionLabel)}</h4>
                ${specsHtml}
              </div>`
            : ''
        }
      </div>
    `;
  };

  const closeDrawer = (drawer = activeDrawer) => {
    if (!drawer || drawer.classList.contains('is-closing')) return;
    if (!drawer.classList.contains('is-open') && drawer.hidden) return;

    const panel = drawer.querySelector('[data-vpl2-drawer-panel]');

    const finishClose = () => {
      drawer.classList.remove('is-active', 'is-closing', 'is-open');
      drawer.hidden = true;
      document.body.classList.remove('vpl2-drawer-open');

      if (activeDrawer === drawer) activeDrawer = null;

      if (lastFocusedElement) {
        lastFocusedElement.focus();
        lastFocusedElement = null;
      }
    };

    drawer.classList.add('is-closing');
    drawer.classList.remove('is-open');

    if (!panel) {
      finishClose();
      return;
    }

    let closed = false;
    const onTransitionEnd = (event) => {
      if (closed) return;
      if (event.target !== panel) return;
      if (event.propertyName && event.propertyName !== 'transform') return;

      closed = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    };

    panel.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(() => {
      if (closed) return;
      closed = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    }, 400);
  };

  const openDrawer = (root) => {
    const drawer = getDrawerForRoot(root);
    const body = drawer?.querySelector('[data-vpl2-drawer-body]');
    if (!drawer || !body) return;

    const selectedTitle =
      [...root.querySelectorAll('[data-vpl2-swatch-input]:checked')].find(isVisibleOption)?.value || '';
    const entry = (root._vpl2Entries || []).find((item) => item.title === selectedTitle);

    // Do not fall back to an unrelated first entry (e.g. other Material).
    if (!entry) return;

    body.innerHTML = buildDrawerContent(entry, drawer);
    body.querySelectorAll('[data-vpl2-color]').forEach((element) => {
      const color = element.getAttribute('data-vpl2-color');
      if (color) element.style.backgroundColor = color;
    });

    if (!lastFocusedElement) {
      lastFocusedElement = document.activeElement;
    }

    drawer.hidden = false;
    drawer.classList.remove('is-closing');
    drawer.classList.add('is-active');
    document.body.classList.add('vpl2-drawer-open');
    activeDrawer = drawer;

    // Force reverse-ready start position, then animate in.
    drawer.classList.remove('is-open');
    void drawer.offsetWidth;

    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
    });

    const closeButton = drawer.querySelector('.vpl2-drawer__close');
    closeButton?.focus();
  };

  const renderCustomSwatch = (root) => {
    const dataNode = root.querySelector('[data-vpl2-swatch-data]');
    const categoryGrid = root.querySelector('[data-vpl2-categories]');
    const swatchGrid = root.querySelector('[data-vpl2-swatch-grid]');
    if (!dataNode || !categoryGrid || !swatchGrid) return;

    let entries = [];
    try {
      entries = JSON.parse(dataNode.textContent || '[]');
    } catch (error) {
      console.error('Custom swatch JSON parse failed', error);
      return;
    }

    if (!Array.isArray(entries) || !entries.length) return;

    const sectionId = root.getAttribute('data-vpl2-section') || 'swatch';
    const categoryMap = new Map();
    const normalizedEntries = entries
      .map((entry) => {
        const title = String(entry.title || '').trim();
        if (!title) return null;

        let categories = normalizeCategories(entry.categories);
        if (!categories.length) categories = ['All'];

        const material = String(entry.material || '').trim();
        const availabilities = normalizeAvailabilityValues(entry.availability);

        categories.forEach((category) => {
          if (!categoryMap.has(category)) {
            categoryMap.set(category, {
              color: entry.color || '',
              materials: new Set(),
              availabilities: new Set(),
            });
          }

          const categoryData = categoryMap.get(category);
          if (!categoryData.color && entry.color) categoryData.color = entry.color;
          if (material) categoryData.materials.add(material);
          availabilities.forEach((availability) => categoryData.availabilities.add(availability));
        });

        return {
          ...entry,
          title,
          material,
          availabilities,
          categories,
          inStockOnProduct: Boolean(entry.inStockOnProduct),
        };
      })
      .filter(Boolean);

    root._vpl2Entries = normalizedEntries;

    const categories = [...categoryMap.keys()].sort((a, b) => {
      const aBeige = normalizeToken(a) === 'beige';
      const bBeige = normalizeToken(b) === 'beige';
      return aBeige === bBeige ? 0 : aBeige ? -1 : 1;
    });
    const selectedCategory = categories[0] || '';

    categoryGrid.innerHTML = categories
      .map((category, index) => {
        const inputId = `CustomCategory-${sectionId}-${index}`;
        const categoryData =
          categoryMap.get(category) || { color: '', materials: new Set(), availabilities: new Set() };
        const color = categoryData.color || '';
        const materials = [...categoryData.materials].join('||');
        const availabilities = [...categoryData.availabilities].join('||');
        const checked = category === selectedCategory ? 'checked' : '';
        const activeClass = category === selectedCategory ? ' is-active' : '';

        return `
          <li
            class="vpl2-swatch-item"
            data-vpl2-category-item
            data-category="${escapeHtml(category)}"
            data-materials="${escapeHtml(materials)}"
            data-availabilities="${escapeHtml(availabilities)}"
          >
            <input
              type="radio"
              class="vpl2-input"
              name="custom-category-${escapeHtml(sectionId)}"
              id="${escapeHtml(inputId)}"
              value="${escapeHtml(category)}"
              data-vpl2-category-input
              ${checked}
            >
            <label class="vpl2-swatch-card${activeClass}" for="${escapeHtml(inputId)}">
              <span class="vpl2-swatch-circle" ${color ? `data-vpl2-color="${escapeHtml(color)}"` : ''}></span>
              <span class="vpl2-swatch-label">${escapeHtml(category)}</span>
            </label>
          </li>
        `;
      })
      .join('');

    swatchGrid.innerHTML = normalizedEntries
      .map((entry, index) => {
        const categoryTokens = entry.categories.join('||');
        const availabilityTokens = entry.availabilities.join('||');
        const inputId = `CustomSwatch-${sectionId}-${entry.id || index}`;
        const media = entry.image
          ? `<img class="vpl2-fabric-image" src="${escapeHtml(entry.image)}" alt="${escapeHtml(entry.title)}" width="120" height="120" loading="lazy">`
          : `<span class="vpl2-fabric-fallback" ${entry.color ? `data-vpl2-color="${escapeHtml(entry.color)}"` : ''}></span>`;

        return `
          <li
            class="vpl2-fabric-item"
            data-vpl2-swatch-item
            data-material="${escapeHtml(entry.material || '')}"
            data-availabilities="${escapeHtml(availabilityTokens)}"
            data-categories="${escapeHtml(categoryTokens)}"
            data-title="${escapeHtml(entry.title)}"
            data-in-stock-on-product="${entry.inStockOnProduct ? 'true' : 'false'}"
          >
            <input
              type="radio"
              class="vpl2-input"
              name="custom-swatch-${escapeHtml(sectionId)}"
              id="${escapeHtml(inputId)}"
              value="${escapeHtml(entry.title)}"
              data-vpl2-swatch-input
              data-category="${escapeHtml(entry.categories[0] || '')}"
            >
            <label class="vpl2-fabric-card" for="${escapeHtml(inputId)}">
              <span class="vpl2-fabric-media">${media}</span>
              <span class="vpl2-fabric-label">${escapeHtml(entry.title)}</span>
            </label>
          </li>
        `;
      })
      .join('');

    root.querySelectorAll('[data-vpl2-color]').forEach((element) => {
      const color = element.getAttribute('data-vpl2-color');
      if (color) element.style.backgroundColor = color;
    });

    filterCustomSwatches(root);
  };

  const initCustomSwatch = (root) => {
    if (!root || root.dataset.vpl2Ready === 'true') return;
    root.dataset.vpl2Ready = 'true';
    renderCustomSwatch(root);
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-vpl2-drawer-close]')) {
      closeDrawer(event.target.closest('[data-vpl2-drawer]'));
      return;
    }

    // Re-clicking an already selected swatch should still open the drawer.
    const fabricCard = event.target.closest('[data-vpl2-swatch-item] .vpl2-fabric-card');
    if (fabricCard) {
      const root = getCustomSwatchRoot(fabricCard);
      const input = fabricCard.htmlFor
        ? document.getElementById(fabricCard.htmlFor)
        : null;

      if (root && input?.checked) {
        openDrawer(root);
      }
    }

    const toggle = event.target.closest('[data-vpl2-toggle]');
    if (!toggle) return;

    const picker = toggle.closest('[data-vpl2-picker]');
    if (!picker) return;

    const selectedRow = toggle.closest('[data-vpl2-row]');
    if (!selectedRow) return;

    const shouldOpen = !selectedRow.classList.contains('is-open');

    picker.querySelectorAll('[data-vpl2-row]').forEach((row) => {
      const button = row.querySelector('[data-vpl2-toggle]');
      row.classList.remove('is-open');
      button?.setAttribute('aria-expanded', 'false');
    });

    if (shouldOpen) {
      selectedRow.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');

      if (isCustomSwatchTriggerRow(selectedRow)) {
        openCustomSwatchRows(picker);
      } else if (selectedRow.hasAttribute('data-vpl2-custom-swatch')) {
        openMaterialRows(picker);
      }
    } else if (selectedRow.hasAttribute('data-vpl2-custom-swatch')) {
      closeDrawer(getDrawerForRoot(selectedRow));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeDrawer) {
      closeDrawer(activeDrawer);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target;

    if (input.matches('[data-vpl2-picker] input[data-variant-input]')) {
      if (!input.checked) return;

      const picker = input.closest('[data-vpl2-picker]');
      const row = input.closest('[data-vpl2-row]');
      if (!picker || !row) return;

      const rowIndex = row.getAttribute('data-vpl2-row');
      const displayLabel = picker.querySelector(`[data-vpl2-sel-display="${rowIndex}"]`);
      if (displayLabel) displayLabel.textContent = input.value;

      row.querySelectorAll('.vpl2-btn-label, .vpl2-swatch-card').forEach((label) => {
        label.classList.remove('is-active');
      });
      document.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.classList.add('is-active');

      // Scope: Material/Materials and Availability options update Custom Swatch filters.
      const optionName = input.getAttribute('name');
      if (isMaterialOptionName(optionName) || isAvailabilityOptionName(optionName)) {
        if (isAvailabilityOptionName(optionName)) {
          updateAvailabilityShippingMessage(picker);
        }

        picker.querySelectorAll('[data-vpl2-custom-swatch]').forEach((customRoot) => {
          filterCustomSwatches(customRoot);

          const drawer = getDrawerForRoot(customRoot);
          if (
            drawer &&
            (drawer.classList.contains('is-open') || drawer.classList.contains('is-active'))
          ) {
            closeDrawer(drawer);
          }
        });

        openCustomSwatchRows(picker);
      }
      return;
    }

    const customRoot = getCustomSwatchRoot(input);
    if (!customRoot) return;

    if (input.matches('[data-vpl2-category-input]')) {
      syncLabelState(input);
      filterCustomSwatches(customRoot);
      return;
    }

    if (input.matches('[data-vpl2-swatch-input]')) {
      syncLabelState(input);
      updateCustomSwatchSummary(customRoot);
      openDrawer(customRoot);
    }
  });

  // Keep line-item properties in sync right before Add to Cart builds FormData.
  // No-op on forms without a Custom Swatch block (standard PDPs, gift cards, etc.).
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const roots = getCustomSwatchRootsForForm(form);
      if (!roots.length) return;

      roots.forEach((root) => {
        updateCustomSwatchSummary(root);
      });
    },
    true
  );

  // Inject selected Custom Swatch values into the cart payload FormData.
  // Scoped to product forms that include [data-vpl2-custom-swatch].
  document.addEventListener('formdata', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    syncCustomSwatchPropertiesToFormData(event.target, event.formData);
  });

  let refreshQueued = false;
  const refreshLayout2Pickers = () => {
    const pickers = getLayout2Pickers();
    // No-op on pages/sections without layout2 picker.
    if (!pickers.length) return;

    // Collapse bursty theme variant events into one sync pass.
    if (refreshQueued) return;
    refreshQueued = true;

    window.requestAnimationFrame(() => {
      refreshQueued = false;
      getLayout2Pickers().forEach((picker) => {
        syncPickerState(picker);
      });
    });
  };

  const getPickerSelectedByIndex = (picker) => {
    const selectedMap = new Map();
    picker.querySelectorAll('input[data-variant-input]:checked').forEach((input) => {
      const indexKey = String(input.getAttribute('data-index') || '').trim();
      if (!indexKey) return;
      selectedMap.set(indexKey, String(input.value || ''));
    });
    return selectedMap;
  };

  const variantDetailMatchesPicker = (detail, picker) => {
    const selectedValues = Array.isArray(detail?.currentlySelectedValues)
      ? detail.currentlySelectedValues
      : [];
    if (!selectedValues.length) return false;

    const pickerSelection = getPickerSelectedByIndex(picker);
    if (!pickerSelection.size) return false;

    return selectedValues.every((item) => {
      const indexKey = String(item?.index || '');
      if (!indexKey) return false;
      return pickerSelection.get(indexKey) === String(item?.value || '');
    });
  };

  const handleVariantChange = (event) => {
    const detail = event?.detail || {};
    const variantAvailable =
      typeof detail?.variant?.available === 'boolean' ? detail.variant.available : null;
    const pickers = getLayout2Pickers();

    if (variantAvailable !== null && pickers.length) {
      let matched = false;

      pickers.forEach((picker) => {
        if (!variantDetailMatchesPicker(detail, picker)) return;
        matched = true;
        pickerVariantAvailability.set(picker, variantAvailable);
      });

      // Fallback for single layout2 picker contexts where detail matching is incomplete.
      if (!matched && pickers.length === 1) {
        pickerVariantAvailability.set(pickers[0], variantAvailable);
      }
    }

    refreshLayout2Pickers();
  };

  // Theme updates option radios without a native change event.
  // Only `variant:change` is dispatched on document by theme.js.
  document.addEventListener('variant:change', handleVariantChange);

  // Checkout/back (bfcache) restores DOM without re-running DOMContentLoaded.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    refreshLayout2Pickers();
  });

  const boot = () => {
    document.querySelectorAll('[data-vpl2-custom-swatch]').forEach((root) => {
      if (isMtoPicker(root)) return;
      initCustomSwatch(root);
    });
    refreshLayout2Pickers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
